import React, { PropsWithChildren, HTMLAttributes } from 'react';
import classnames from 'classnames';

import { isPingIdentityEnabled } from '../../../../../helpers/authorization/isPingIdentityEnabled';

import {
  MobileLayouts,
  selectSiteHeader,
} from '../../../../../store/features/siteHeader';
import { selectSiteFooter } from '../../../../../store/features/siteFooter';
import { selectOptions } from '../../../../../store/features/options';
import { selectSiteType } from '../../../../../store/features/siteType';
import { selectAdvertisingPageCategory } from '../../../../../utils/advertising/selectAdvertisingPageCategory';
import { selectPageConfig } from '../../../../../store/features/pageConfig';

import InformaRibbon from '../../../../common/InformaRibbon/InformaRibbon';
import Advertising from '../../../../hub/shared/Advertising';
import SiteFooter from '../../../../layouts/SiteLayout/SiteFooter/SiteFooter';
import NavigationMenu from '../NavigationMenu';
import Header from '../Header';
import InformaPingIdentityRibbon from '../../../../common/InformaPingIdentityRibbon/InformaPingIdentityRibbon';

import { useSetUserDetailsToDataLayer } from '../../../../hub/shared/hooks';
import { useRootSelector } from '../../../../../store/hooks';
import { useCheckVIPCode } from '../../../hooks/discount/useCheckVIPCode';
import useCheckBasket from '../../../hooks/basket/useCheckBasket';

import styles from './Layout.module.scss';

export interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
  disableNavMenu?: boolean;
  disableHeader?: boolean;
  disableUpperFooter?: boolean;
}

export default function Layout(props: PropsWithChildren<LayoutProps>) {
  const {
    disableNavMenu,
    disableHeader,
    disableUpperFooter,
    className,
    children,
  } = props;

  useSetUserDetailsToDataLayer();
  useCheckVIPCode();
  useCheckBasket();

  const siteHeader = useRootSelector(selectSiteHeader);
  const siteFooter = useRootSelector(selectSiteFooter);
  const siteOptions = useRootSelector(selectOptions);
  const { type: siteType } = useRootSelector(selectSiteType);
  const { siteType: pageConfigSiteType } = useRootSelector(selectPageConfig);
  const pageCategory = useRootSelector(selectAdvertisingPageCategory);

  const {
    data: { mobileLayout, hideInformaBar },
  } = siteHeader;
  const { advertisingEnabled, networkId, adUnit, pingAuthIdentityEnabled } =
    siteOptions;
  const isPingIdentityFlow = isPingIdentityEnabled(
    pageConfigSiteType,
    pingAuthIdentityEnabled,
  );

  const wrapperClassNames = classnames(className, styles.wrapper, {
    // l-site-[siteType] class name was added to support CSS overrides for the CallToAction
    [`l-site-${siteType?.toLowerCase()}`]: !!siteType,
  });
  const topHeaderClass = classnames({
    'hidden-xs': mobileLayout === MobileLayouts.BOTTOM,
  });
  const bottomHeaderClass = classnames({
    'visible-xs': mobileLayout === MobileLayouts.BOTTOM,
    hidden: mobileLayout === MobileLayouts.TOP,
  });

  return (
    <div className={wrapperClassNames}>
      <div className={styles.layoutWrapper}>
        {!hideInformaBar && (
          <>
            {isPingIdentityFlow ? (
              <InformaPingIdentityRibbon />
            ) : (
              <InformaRibbon />
            )}
          </>
        )}
        {advertisingEnabled && (
          <Advertising
            adUnit={adUnit}
            networkId={networkId}
            pageCategory={pageCategory}
            advSlots={['728_1']}
            advSize="728_90"
          />
        )}

        <div className={styles.contentWrapper}>
          {!disableHeader && (
            <div className={topHeaderClass}>
              <Header />
            </div>
          )}

          {!disableNavMenu && <NavigationMenu />}

          {!disableHeader && (
            <div className={bottomHeaderClass}>
              <Header />
            </div>
          )}

          <main className={styles.contentWrapper} id="content">
            {children}
          </main>

          <SiteFooter
            footer={siteFooter}
            footerUpperHidden={disableUpperFooter}
            advertisingEnabled={advertisingEnabled}
          />
        </div>
      </div>
    </div>
  );
}
